import { PaymentMethodCode, type PaymentMethod } from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import { useAuth } from '@ahm/react-auth';
import {
  Avatar,
  Badge,
  Button,
  Divider,
  Group,
  Indicator,
  Radio,
  Stack,
  Text,
  rem,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';
import styles from '@/components/RadioButton.module.css';
import { usePaymentMethodHook } from '@/hooks';
import { convertPaymentMethodNumber } from '@/lib/utils';
import { useLocaleSuffix } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

export type NewPaymentMethod = Pick<
  PaymentMethod,
  | 'name_vi_vn'
  | 'name_en_us'
  | 'description_vi_vn'
  | 'description_en_us'
  | 'method_id'
  | 'icon_url'
  | 'code'
  | 'extra_data'
>;

type PaymentSelectionItemProps = {
  method: PaymentMethod | NewPaymentMethod;
  onSelect: (methodId: string) => void;
};

const PaymentSelectionItem = ({
  method,
  onSelect,
}: PaymentSelectionItemProps) => {
  const tPayment = useTranslations('payment');
  const localSuffix = useLocaleSuffix();
  const { session } = useAuth();
  const user = session?.user;
  const { needLinkMethod } = usePaymentMethodHook();

  const isMobile = useMediaQuery('(max-width: 768px)');

  const { estimatedData } = useBookingStore((state) => state);

  const errorMess = (method as PaymentMethod)[`error_message_${localSuffix}`];

  const isBalancePM = useMemo(
    () => method.code === PaymentMethodCode.Balance.toLowerCase(),
    [method]
  );
  const isBusinessAccountPM = useMemo(
    () => method.code === PaymentMethodCode.BusinessAccount.toLowerCase(),
    [method]
  );

  const isCashPM = useMemo(
    () => method.code === 'cash' || method.code === 'cash_by_recipient',
    [method]
  );

  const getPaymentMethodDescription = useCallback(() => {
    if (!method || isCashPM) return;
    if (errorMess) {
      return errorMess;
    }

    const isShowTotalBusinessAccount =
      isBusinessAccountPM &&
      estimatedData?.data?.total_business_account_balance &&
      estimatedData?.data?.total_business_account_balance > 0;

    if (isBalancePM) {
      if (
        estimatedData?.data?.total_balance &&
        estimatedData?.data?.total_balance > 0
      )
        return `${tPayment('select_method.balance')}: ${formatCurrency(estimatedData?.data?.total_balance)}`;
      if (user && user.main_account.VND + user.bonus_account.VND > 0)
        return `${tPayment('select_method.balance')}: ${formatCurrency(
          user.main_account.VND + user.bonus_account.VND
        )}`;
    } else if (isShowTotalBusinessAccount) {
      return `${tPayment('select_method.balance')}: ${formatCurrency(estimatedData.data.total_business_account_balance)}`;
    } else {
      return method[`description_${localSuffix}`];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    errorMess,
    estimatedData?.data,
    isBalancePM,
    isBusinessAccountPM,
    isCashPM,
    method,
    user,
  ]);

  // const paymentTitle = useCallback(() => {
  //   return method?.extra_data?.card_number
  //     ? convertPaymentMethodNumber(method?.extra_data?.card_number)
  //     : method[`name_${localSuffix}`];
  // }, [method, localSuffix]);

  const paymentTitle = useCallback(() => {
    const extraData = method?.extra_data;
    const baseName = method[`name_${localSuffix}`];

    const paymentId =
      extraData?.card_number ?? extraData?.masked_payment_identifier;

    if (paymentId) {
      return `${baseName} ${convertPaymentMethodNumber(paymentId)}`;
    }

    return baseName;
  }, [method, localSuffix]);

  return (
    <>
      <Button
        component="div"
        fullWidth
        variant="subtle"
        color="neutral"
        py={rem(12)}
        px={isMobile ? rem(16) : rem(24)}
        mih={rem(64)}
        onClick={() => !errorMess && onSelect(method.method_id)}
        radius={0}
        styles={{
          inner: {
            justifyContent: 'flex-start',
          },
          label: {
            width: '100%',
            flex: '1 1 auto',
          },
        }}
        // leftSection={
        //   <>
        //     {method.method_id === '01' ? (
        //       <DomesticCard radius={4} height={24} width={24} />
        //     ) : method.method_id === '02' ? (
        //       <InternationalCard radius={4} height={24} width={24} />
        //     ) : errorMess ? (
        //       <Indicator size={12} label="!" position="bottom-end" color="red">
        //         <Avatar
        //           radius={4}
        //           size={24}
        //           src={method.icon_url}
        //           alt={paymentTitle()}
        //         />
        //       </Indicator>
        //     ) : (
        //       <Avatar
        //         radius={4}
        //         size={24}
        //         src={method.icon_url}
        //         alt={paymentTitle()}
        //       />
        //     )}
        //   </>
        // }
        leftSection={
          <Indicator
            disabled={!errorMess}
            size={12}
            label="!"
            position="bottom-end"
            color="red"
          >
            <Avatar
              radius={4}
              size={24}
              src={method.icon_url}
              alt={paymentTitle()}
            />
          </Indicator>
        }
      >
        <Radio
          variant="outline"
          classNames={{ radio: styles.radioBtn }}
          disabled={!!errorMess}
          styles={{
            body: {
              width: '100%',
              justifyContent: 'space-between',
              alignItems: 'center',
            },
            inner: { alignSelf: 'center' },
          }}
          w="100%"
          labelPosition="left"
          value={method.method_id}
          label={
            <Group mih={40} styles={{ root: { cursor: 'pointer' } }}>
              <Stack align="left" gap={rem(0.4)}>
                <Text
                  component="div"
                  fw={700}
                  size="sm"
                  ta="left"
                  c={errorMess ? 'neutral.5' : 'neutral.7'}
                >
                  {paymentTitle()}{' '}
                  {needLinkMethod(method as PaymentMethod) && (
                    <Badge tt="none" color="neutral.1" fw={500}>
                      {tPayment('not_linked_yet')}
                    </Badge>
                  )}
                </Text>
                <Text size="xs" fw={600} ta="left">
                  {getPaymentMethodDescription()}
                </Text>
              </Stack>
            </Group>
          }
        />
        <Divider />
      </Button>
    </>
  );
};
export default PaymentSelectionItem;
